import { useEffect } from 'react';
import Constants from 'expo-constants';
import useStore from '@/stores/useStore';
import useConfigStore from '@/stores/useConfigStore';
import { IS_WEB } from '@/shared/constants/utility';
import SentryService from '@/services/sentry/Sentry.service';

interface VersionCheckResponse {
  currentVersion: string;
  minimumVersion: string;
  updateRequired: boolean;
}

export const useAppVersionCheck = () => {
  const { setIsAppVersionModalVisible } = useStore();
  const environment = useConfigStore((state) => state.environment);

  useEffect(() => {
    // Only run version check in mobile app
    if (IS_WEB) {
      return;
    }

    const checkVersion = async () => {
      try {
        const currentVersion = Constants.expoConfig?.version;

        if (!currentVersion) {
          throw new Error(
            'App version not found in app.json. Please ensure version field is set.',
          );
        }

        const response = await fetch(
          `${environment.server.baseUrl}/api/version/check`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              version: currentVersion,
            }),
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data: VersionCheckResponse = await response.json();

        if (data.updateRequired) {
          setIsAppVersionModalVisible(true);
          SentryService.captureInfo('App version check: Update required', {
            extra: {
              currentVersion,
              minimumVersion: data.minimumVersion,
            },
          });
        }
      } catch (error) {
        // Silently fail - don't block the app if version check fails
        SentryService.captureError('Failed to check app version', error);
      }
    };

    checkVersion();
  }, [environment.server.baseUrl, setIsAppVersionModalVisible]);
};
